import {Bounds2} from "@utility/bounds2";

export class Vector2 {
    x: number = 0;
    y: number = 0;

    static one = new Vector2(1, 1);
    static zero = new Vector2(0, 0);

    constructor(arg1: any, arg2?: number) {
        if (arg1.x !== undefined) {
            this.x = arg1.x;
            this.y = arg1.y;
        } else if ((arg1 as number[]).length) {
            this.serialize(arg1);
        } else {
            this.x = arg1 as number;
            this.y = arg2 as number;
        }
    }
    
    abs() {
        return new Vector2(Math.abs(this.x), Math.abs(this.y));
    }
    axial() {
        return new Vector2(this.x - Math.ceil(this.y * 0.5), this.y);
    }
    clamp(bounds: Bounds2) {
        return new Vector2(
            Math.min(bounds.max.x, Math.max(bounds.min.x, this.x)),
            Math.min(bounds.max.y, Math.max(bounds.min.y, this.y))
        );
    }
    divide(divisor: number | Vector2) {
        if (divisor instanceof Vector2) {
            return new Vector2({x: this.x / divisor.x, y: this.y / divisor.y});
        } else {
            return new Vector2({x: this.x / divisor, y: this.y / divisor});
        }
    }
    dot(compare: Vector2) {
        return this.x * compare.x + this.y * compare.y; 
    }
    equals(compare: Vector2) {
        return this.x === compare.x && this.y === compare.y;
    }
    floor() {
        return new Vector2(Math.floor(this.x), Math.floor(this.y));
    }
    greater(compare: Vector2) {
        return this.x > compare.x && this.y > compare.y;
    }
    greaterOrEqual(compare: Vector2) {
        return this.x >= compare.x && this.y >= compare.y;
    }
    length() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }
    lengthSquared() {
        return this.x * this.x + this.y * this.y;
    }
    less(compare: Vector2) {
        return this.x < compare.x && this.y < compare.y;
    }
    lessOrEqual(compare: Vector2) {
        return this.x <= compare.x && this.y <= compare.y;
    }
    max(compare: Vector2) {
        return new Vector2(Math.max(this.x, compare.x), Math.max(this.y, compare.y));
    }
    maxX(compare: number) {
        return new Vector2(Math.max(this.x, compare), this.y);
    }
    maxY(compare: number) {
        return new Vector2(this.x, Math.max(this.y, compare));
    }
    min(compare: Vector2) {
        return new Vector2(Math.min(this.x, compare.x), Math.min(this.y, compare.y));
    }
    minX(compare: number) {
        return new Vector2(Math.min(this.x, compare), this.y);
    }
    minY(compare: number) {
        return new Vector2(this.x, Math.min(this.y, compare));
    }
    minus(delta: Vector2 | number) {
        if (delta instanceof Vector2) {
            return new Vector2({x: this.x - delta.x, y: this.y - delta.y});
        } else {
            return new Vector2({x: this.x - delta, y: this.y - delta});
        }
    }
    mod(compare: Vector2 | number) {
        if (compare instanceof Vector2) {
            return new Vector2(this.x % compare.x, this.y % compare.y);
        } else {
            return new Vector2(this.x % compare, this.y % compare);
        }
    }
    multiply(multiple: number | Vector2) {
        if (multiple instanceof Vector2) {
            return new Vector2({x: this.x * multiple.x, y: this.y * multiple.y});
        } else {
            return new Vector2({x: this.x * multiple, y: this.y * multiple});
        }
    }
    normalise() {
        return new Vector2({x: this.x === 0 ? 0 : this.x < 0 ? -1 : 1, y: this.y === 0 ? 0 : this.y < 0 ? -1 : 1});
    }
    plus(plus: Vector2 | number) {
        if (plus instanceof Vector2) {
            return new Vector2({x: this.x + plus.x, y: this.y + plus.y});
        } else {
            return new Vector2({x: this.x + plus, y: this.y + plus});
        }
    }
    round() {
        return new Vector2(Math.round(this.x), Math.round(this.y));
    }
    toString() {
        return `{x: ${this.x}, y: ${this.y}}`;
    }
    toStyleLocation() {
        return {
            left: this.x,
            top: this.y
        };
    }
    toStyleSize() {
        return {
            height: this.y,
            width: this.x
        };
    }
    
    // Note that serialise works in both directions - if you pass in coordinates then it writes them
    // if not then it just returns them
    serialize(coordinates?: number[]) {
        if (coordinates) {
            this.x = coordinates[0];
            this.y = coordinates[1];
        }
        return [this.x, this.y];
    }
}